/**
 * TravelSummaryStrip — read-only expandable strip surfacing the
 * math behind a feasible slot. Default collapsed. Expansion is
 * opt-in.
 *
 * DESIGN PASS — STATIC MOCKUP ONLY. Feb 18, 2026.
 *
 * Doctrine sources:
 *   /app/memory/TRAVEL_AWARE_SCHEDULING_PROPOSAL.md §6.4 (operational honesty)
 *   /app/memory/TRAVEL_AWARE_WIREFRAME_PASS.md §3
 *
 * This is the canonical transparency surface — instructors can
 * see EXACTLY how a buffer was derived. No black-box "AI says…"
 * copy. All numerics use `tabular-nums`.
 */
import { useState } from "react";
import { Clock, ChevronRight, ChevronDown } from "lucide-react";

export default function TravelSummaryStrip({
  travelMinutes = 23,
  bufferMinutes = 10,
  distanceMiles = 5.2,
  speedMph = 25,
  overheadMinutes = 5,
  totalMinutes = 33,
  gapDisplay = "2:30",
  postcodeMissing = false,
  defaultExpanded = false,
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  if (postcodeMissing) {
    return (
      <div
        data-testid="travel-summary-strip"
        className="bg-slate-50 border border-slate-200 rounded-md p-3"
      >
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Clock className="w-3.5 h-3.5" />
          Travel estimate unavailable — postcode not set.
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="travel-summary-strip"
      className="bg-slate-50 border border-slate-200 rounded-md"
    >
      <button
        type="button"
        data-testid="travel-summary-toggle"
        onClick={() => setExpanded((v) => !v)}
        className="w-full p-3 flex items-center gap-2 text-sm text-slate-700"
        aria-expanded={expanded}
      >
        <Clock className="w-3.5 h-3.5 text-slate-400 shrink-0" />
        <span className="flex-1 text-left">
          <span className="tabular-nums">{travelMinutes}</span> min travel
          {" · "}
          <span className="tabular-nums">{bufferMinutes}</span> min buffer
        </span>
        {expanded ? (
          <ChevronDown className="w-3.5 h-3.5 text-slate-400 shrink-0" />
        ) : (
          <ChevronRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />
        )}
      </button>

      {expanded && (
        <div
          data-testid="travel-summary-math-breakdown"
          className="px-3 pb-3 pt-0 border-t border-slate-200"
        >
          <dl className="mt-2 space-y-1 text-[12px] text-slate-600 leading-relaxed">
            <div className="flex justify-between">
              <dt><span className="tabular-nums">{distanceMiles}</span> mi at <span className="tabular-nums">{speedMph}</span> mph</dt>
            </div>
            <div className="flex justify-between">
              <dt>+ <span className="tabular-nums">{overheadMinutes}</span> min overhead</dt>
            </div>
            <div className="flex justify-between">
              <dt>+ <span className="tabular-nums">{bufferMinutes}</span> min buffer</dt>
            </div>
            <div className="border-t border-slate-200 my-1" />
            <div className="flex justify-between text-slate-900 font-bold">
              <dt>= <span className="tabular-nums">{totalMinutes}</span> min total</dt>
            </div>
          </dl>

          <p
            data-testid="travel-summary-context"
            className="mt-3 text-[12px] text-slate-500 leading-relaxed"
          >
            <span className="tabular-nums">{gapDisplay}</span> available
            to allow travel time between lessons.
          </p>
        </div>
      )}
    </div>
  );
}
